/**
 * File d'écritures hors ligne (US-12, §1 CLAUDE.md : « les écritures hors
 * ligne sont mises en file et rejouées à la reconnexion » ; DECISIONS.md,
 * « US-08 » : chantier explicitement rattaché à US-12). Ne couvre que les
 * écritures au corps JSON (création de recette, US-01 ; édition de fiche,
 * ingrédients, étapes, US-06 ; réalisation, US-10) — pas les envois de photo
 * (`multipart/form-data`) : sérialiser un fichier dans la file dépasserait ce
 * que l'AC demande, et une photo reste ajoutable après coup une fois la
 * recette elle-même synchronisée (dégradation identique aux autres API
 * indisponibles hors ligne, ex. le repli LLM d'US-02).
 *
 * `localStorage`, pas IndexedDB : le volume est celui d'un usage personnel
 * (quelques écritures entre deux reconnexions, §1 CLAUDE.md), et
 * `localStorage` est synchrone et déjà utilisé par ce projet pour un état
 * qui doit survivre à la fermeture de l'onglet (`mlc:minuteurs`, US-09) —
 * même raisonnement ici : une recette saisie hors ligne ne doit pas se perdre
 * si l'onglet est refermé avant la reconnexion.
 */

export type QueuedMethod = 'POST' | 'PATCH' | 'PUT';

export interface QueuedMutation {
  id: string;
  method: QueuedMethod;
  url: string;
  body: unknown;
  queued_at: string;
  /**
   * Membre qui a produit l'écriture (US-47, cas 14).
   *
   * Si quelqu'un se déconnecte et qu'un autre se connecte avant le rejeu, les
   * écritures du premier seraient attribuées au second : la recette
   * apparaîtrait sous le mauvais nom, et la réalisation dans le mauvais
   * journal. Rare, silencieux, et il corrompt l'attribution — donc à traiter.
   *
   * `null` pour une entrée mise en file avant cette story : elle est rejouée
   * comme avant, faute de mieux, plutôt que perdue.
   */
  member_id: string | null;
}

const STORAGE_KEY = 'mlc:file-hors-ligne';

function readQueue(): QueuedMutation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as QueuedMutation[]) : [];
  } catch {
    return []; // stockage indisponible ou corrompu : dégradation silencieuse, même principe que servingsStorage.ts.
  }
}

function writeQueue(queue: QueuedMutation[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // Mémorisation en confort : une écriture refusée (quota, navigation privée stricte) ne doit pas casser l'écran.
  }
}

/** Ajoute une mutation à la file, à l'échec réseau (cf. `postOrQueue`, api.ts). */
export function enqueueMutation(
  method: QueuedMethod,
  url: string,
  body: unknown,
  memberId: string | null = readCurrentMember(),
): QueuedMutation {
  const entry: QueuedMutation = {
    id: crypto.randomUUID(),
    method,
    url,
    body,
    queued_at: new Date().toISOString(),
    member_id: memberId,
  };
  writeQueue([...readQueue(), entry]);
  return entry;
}

/** Le membre dont les traces sont sur cet appareil (voir `memberScope.ts`). */
function readCurrentMember(): string | null {
  try {
    return localStorage.getItem('mlc:membre-courant');
  } catch {
    return null;
  }
}

export function listQueuedMutations(): QueuedMutation[] {
  return readQueue();
}

export function queueLength(): number {
  return readQueue().length;
}

export interface ReplayResult {
  replayed: number;
  remaining: number;
}

/**
 * Rejoue la file dans l'ordre d'ajout (§ FIFO : une recette créée avant une
 * autre doit être synchronisée avant elle). Une entrée encore hors de portée
 * du réseau reste en file pour le prochain essai ; une entrée rejetée par le
 * serveur (ex. validation) est retirée — la rejouer indéfiniment ne la
 * rendrait jamais valide, et l'utilisateur a de toute façon quitté l'écran qui
 * aurait pu afficher l'erreur.
 */
export async function replayQueuedMutations(fetchImpl: typeof fetch = fetch): Promise<ReplayResult> {
  const queue = readQueue();
  const stillQueued: QueuedMutation[] = [];
  const membreCourant = readCurrentMember();
  let replayed = 0;

  for (const entry of queue) {
    // **Une écriture d'un autre membre n'est pas rejouée, elle est
    // abandonnée** (US-47, cas 14). La rejouer l'attribuerait à qui est
    // connecté maintenant : la recette apparaîtrait sous le mauvais nom et la
    // réalisation dans le mauvais journal. Perdre une écriture est visible et
    // se refait ; l'attribuer à la mauvaise personne ne se voit pas.
    if (entry.member_id !== null && membreCourant !== null && entry.member_id !== membreCourant) continue;
    try {
      await fetchImpl(entry.url, {
        method: entry.method,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(entry.body),
      });
      replayed += 1;
    } catch {
      stillQueued.push(entry); // toujours hors connexion (ou de nouveau) : reste en file, prochain essai.
    }
  }

  writeQueue(stillQueued);
  return { replayed, remaining: stillQueued.length };
}
